import { i18n } from '@/plugins/vueI18n'
import { throttle } from 'lodash-es'

type I18nGlobalTranslation = {
  (key: string): string
  (key: string, locale: string): string
  (key: string, locale: string, list: unknown[]): string
  (key: string, locale: string, named: Record<string, unknown>): string
  (key: string, list: unknown[]): string
  (key: string, named: Record<string, unknown>): string
}

const getKey = (namespace: string | undefined, key: string) => {
  if (!namespace) {
    return key
  }
  if (key.startsWith(namespace)) {
    return key
  }
  return `${namespace}.${key}`
}

function isCapitalized(word: string): boolean {
  return word.charAt(0) === word.charAt(0).toUpperCase()
}

/**
 * 转换：
 * abc -> abc
 * Abc -> abc
 * ABC -> ABC
 */
function convertString(word: string): string {
  if (word === word.toUpperCase()) {
    // 如果单词全部是大写，则保持不变
    return word
  } else if (isCapitalized(word)) {
    // 如果单词首字母大写，则转换为全部小写
    return word.toLowerCase()
  } else {
    // 其他情况，保持原样
    return word
  }
}

export const useI18n = (
  namespace?: string
): {
  t: I18nGlobalTranslation
} => {
  // 使用一个共用函数来处理翻译逻辑
  const translate = (key: string, originalT: any, args: any[]) => {
    try {
      return originalT(key, ...args)
    } catch (error) {
      console.error('Translation error:', error)
      return '' // 根据实际情况，这里也可以考虑抛出异常
    }
  }

  const getLang = throttle(() => {
    return localStorage.getItem('lang')
  }, 500)

  const normalFn = {
    t: (key: string) => {
      // 增加异常处理
      try {
        return getKey(namespace, key)
      } catch (error) {
        console.error('getKey error:', error)
        return '' // 根据实际情况，这里也可以考虑抛出异常
      }
    },
    mt: (keys: string[]) => {
      try {
        const result: string[] = []
        for (const key of keys) {
          result.push(getKey(namespace, key))
        }
        return result
      } catch (error) {
        return ''
      }
    }
  }

  if (!i18n) {
    return normalFn
  }

  const { t: originalT, ...methods } = i18n.global

  const tFn: I18nGlobalTranslation = (key: string, ...arg: any[]) => {
    if (!key) return ''
    // 确保即使没有 '.' 和 namespace，也能正确处理键
    return translate(getKey(namespace, key), originalT, arg)
  }

  // 引入缓存机制优化性能
  const mtCache: { [key: string]: string } = {} // 注意：这里未处理缓存的失效机制

  const mtFn = (keys: string[], ...restParams: any[]): string => {
    // 用于获取lang值，应考虑减少对 localStorage 的直接访问，但是切换语言时，需要重新获取
    const lang = getLang() // 通过节流优化
    const result: string[] = []
    for (const key of keys) {
      const finalKey = namespace ? `${namespace}.${key}` : key
      const cachedResult = mtCache[`${lang}_${finalKey}`]
      if (cachedResult) {
        result.push(cachedResult)
        continue
      }
      try {
        const translatedResult = translate(finalKey, originalT, restParams)
        result.push(translatedResult)
        // 更新缓存
        mtCache[`${lang}_${finalKey}`] = translatedResult
      } catch (error) {
        // 错误处理逻辑
      }
    }
    // 中英文拼接方式区分
    const separator = lang === 'zh-CN' ? '' : ' '
    const convertedResults = result.slice(1).map((item) => convertString(item))
    return result.slice(0, 1).concat(convertedResults).join(separator)
  }

  return {
    ...methods,
    t: tFn,
    mt: mtFn
  }
}

export const t = (key: string) => key
